import React, { useEffect, useState } from 'react';
import { useLocation, Link, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Select,
  MenuItem,
  FormControl,
  Button,
  CircularProgress,
  Alert,
  Chip,
  Divider,
  AlertTitle,
} from '@mui/material';
import { PageIndices, TabIndices } from '../../common/i-doc/i-doc.registry.ts';
import { useTranslation } from 'react-i18next';
import { AdaptiveTabs, AdaptiveTabItem } from '@components/common/AdaptiveTabs.tsx';
import BoltIcon from '@mui/icons-material/Bolt';
import OpacityIcon from '@mui/icons-material/Opacity';
import InfoIcon from '@mui/icons-material/Info';
import TelegramIcon from '@mui/icons-material/Telegram';
import SendIcon from '@mui/icons-material/Send';
import { DashboardLayout } from '@components/DashboardLayout.tsx';
// @ts-ignore
import * as settingsService from '@services/settings.service.ts';
import { DimensionSetting, UpdateDimensionSettingPayload, DailyNormsResponse, UsedData } from '@/types/settings.types';
import { useAuth } from '@hooks/useAuth.ts';
import { ProfileSettingsTab } from '@pages/Settings/ProfileSettingsTab.tsx';
import { GoalsSettingsTab } from '@pages/Settings/GoalsSettingsTab.tsx';
import { AccessSettingsTab } from '@pages/Settings/AccessSettingsTab.tsx';
import { SubscriptionSettingsTab } from '@pages/Settings/SubscriptionSettingsTab.tsx';
import { ExpertSubscriptionSettingsTab } from '@pages/Settings/ExpertSubscriptionSettingsTab.tsx';
import { TelegramSettingsTab } from '@pages/Settings/TelegramSettingsTab.tsx';
import { InterfaceSettingsTab } from '@pages/Settings/InterfaceSettingsTab.tsx';
import { MailingsSettingsTab } from '@pages/Settings/MailingsSettingsTab.tsx';
import { PageHint } from '@components/common/PageHint.tsx';
import { toastService } from '@services/toast.service.ts';
import { MetricInfoModal } from '@components/CalculatedMetrics/MetricInfoModal.tsx';

interface TabPanelProps {
  children?: React.ReactNode;
  index: string;
  value: string;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

const renderUsedData = (usedData: UsedData | undefined) => { 
  if (!usedData) return <Typography variant="body2">Дані відсутні</Typography>;

  const dataToRender = {
    ...usedData.atomic,
    ...usedData.calculated,
    goal: usedData.goal,
    dietScheme: usedData.dietScheme,
  };

  return (
    <Box>
      {Object.entries(dataToRender).map(([key, value]) => {
        if (!value) return null;
        return (
          <Typography key={key} variant="body2">
            {key.charAt(0).toUpperCase() + key.slice(1)}: <b>{String(value)}</b>
          </Typography>
        );
      })}
    </Box>
  );
};

export const SettingsPage: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const isClient = user?.roles?.includes('client');
  const isExpert = user?.roles?.includes('expert');

  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
  const [selectedMetricForInfo, setSelectedMetricForInfo] = useState<string | null>(null);

  const handleOpenInfoModal = (metric: string) => {
    setSelectedMetricForInfo(metric);
    setIsInfoModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsInfoModalOpen(false);
    setSelectedMetricForInfo(null);
  };

  const tabs: AdaptiveTabItem[] = [
    { value: 'profile', label: t('settings.tabs.profile') },
    { value: 'units', label: t('settings.tabs.units') },
    { value: 'access', label: t('settings.tabs.access') },
    { value: 'interface', label: t('settings.tabs.interface') },
    ...(isClient ? [
      { value: 'goals', label: t('settings.tabs.goals') },
      { value: 'goals-adv', label: t('settings.tabs.goalsAdv') },
      { value: 'subscription', label: t('settings.tabs.subscription') },
    ] : []),
    ...(isExpert ? [
      { value: 'expert-subscription', label: t('settings.tabs.subscription') },
      { value: 'telegram', label: t('settings.tabs.telegram'), icon: <TelegramIcon /> },
      { value: 'mailings', label: t('settings.tabs.mailings'), icon: <SendIcon /> },
    ] : []),
  ];

  const tabParam = searchParams.get('tab');
  const currentTab = (tabParam && tabs.some(t => t.value === tabParam)) ? tabParam : 'profile';

  const [settings, setSettings] = useState<DimensionSetting[]>([]);
  // @ts-ignore
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const [goals, setGoals] = useState<DailyNormsResponse | null>(null);
  const [goalsLoading, setGoalsLoading] = useState(false);
  const [goalsError, setGoalsError] = useState<string | null>(null);

  useEffect(() => {
    const requestedTab = location.state?.activeTab;
    if (requestedTab && requestedTab !== currentTab && tabs.some(t => t.value === requestedTab)) {
      setSearchParams({ tab: requestedTab });
    }
  }, [location.state]);

  useEffect(() => {
    if (currentTab === 'units') {
      const fetchSettings = async () => {
        try {
          setLoading(true);
          const data = await settingsService.getDimensionSettings();
          setSettings(Array.isArray(data) ? data : []);
        } catch (err) {
          setError('Не вдалося завантажити налаштування.');
          console.error(err);
        } finally {
          setLoading(false);
        }
      };

      fetchSettings();
    }
  }, [currentTab, setLoading]);

  useEffect(() => {
    if (currentTab === 'goals') {
      const fetchGoals = async () => {
        try {
          setGoalsLoading(true);
          setGoalsError(null);
          const data = await settingsService.getDailyNorms();
          setGoals(data);
        } catch (err) {
          setGoalsError('Не вдалося завантажити цілі.');
          console.error(err);
        } finally {
          setGoalsLoading(false);
        }
      };

      fetchGoals();
    }
  }, [currentTab]);

  const handleUnitChange = (dimension: string, newUnitId: number) => {
    setSettings(currentSettings =>
      currentSettings.map(setting =>
        setting.dimension === dimension
          ? { ...setting, selectedUnitId: newUnitId }
          : setting
      )
    );
  };

  const handleSave = async () => {
    setIsSaving(true);
    setError(null);

    const payload: UpdateDimensionSettingPayload[] = settings.map(({ dimension, selectedUnitId }) => ({
      dimension,
      unitId: selectedUnitId,
    }));

    try {
      await settingsService.updateDimensionSettings(payload);
      toastService.success('Налаштування успішно збережено');
    } catch (err) {
      toastService.error('Не вдалося зберегти налаштування.');
      console.error(err);
    } finally {
      setIsSaving(false);
    }
  };

  const renderUnitsContent = () => {
    if (loading) {
      return <CircularProgress />;
    }

    if (error && !isSaving) {
      return <Alert severity="error">{error}</Alert>;
    }

    return (
      <Paper sx={{ p: 3 }} data-i-doc={TabIndices.SETTINGS_UNITS}>
        <PageHint iDoc={TabIndices.SETTINGS_UNITS} />
        <Typography variant="h6" gutterBottom>
          Одиниці вимірювання
        </Typography>
        <Box component="div" sx={{ mt: 3 }}>
          {settings
            .filter(setting => setting && Array.isArray(setting.units))
            .map(setting => (
              <Grid container spacing={2} alignItems="center" key={setting.dimension} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={4}>
                  <Typography variant="subtitle1" align="left" sx={{ fontWeight: 'bold' }}>
                    {setting.dimension}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={8}>
                  <FormControl fullWidth>
                    <Select
                      value={setting.selectedUnitId}
                      onChange={(e) => handleUnitChange(setting.dimension, e.target.value as number)}
                    >
                      {setting.units.map(unit => (
                        unit && <MenuItem key={unit.id} value={unit.id}>
                          {unit.name} ({unit.symbol})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            ))}
        </Box>
        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
          <Button variant="contained" onClick={handleSave} disabled={isSaving}>
            {isSaving ? <CircularProgress size={24} /> : 'Зберегти'}
          </Button>
        </Box>
        {error && isSaving && <Alert severity="error" sx={{mt: 2}}>{error}</Alert>}
      </Paper>
    );
  };

  const renderGoalsContent = () => {
    if (goalsLoading) {
      return <CircularProgress />;
    }

    if (goalsError) {
      return <Alert severity="error">{goalsError}</Alert>;
    }

    if (!goals || !goals.data) {
      return <Typography>Немає даних для відображення.</Typography>;
    }

    const { data } = goals;
    const calculationDetails = data.calculationDetails;

    const proteinG = Number(data.proteinG ?? 0);
    const fatG = Number(data.fatG ?? 0);
    const carbsG = Number(data.carbsG ?? 0);

    const proteinCalories = proteinG * 4;
    const fatCalories = fatG * 9;
    const carbsCalories = carbsG * 4;
    const totalNutrientCalories = proteinCalories + fatCalories + carbsCalories;

    const proteinPercentage = totalNutrientCalories > 0 ? (proteinCalories / totalNutrientCalories) * 100 : 0;
    const fatPercentage = totalNutrientCalories > 0 ? (fatCalories / totalNutrientCalories) * 100 : 0;
    const carbsPercentage = totalNutrientCalories > 0 ? (carbsCalories / totalNutrientCalories) * 100 : 0;

    return (
      <Box>
        <Alert severity="info" sx={{ my: 2 }}>
          <AlertTitle>Ваші розраховані денні цілі</AlertTitle>
          Ми проаналізували наявну інформацію і розрахували ваші денні цілі. Ці розрахунки базуються на наших внутрішніх алгоритмах, включаючи ШІ. Для досягнення найкращих результатів та розробки індивідуального плану, радимо звернутися до професіонала.
          <br />
          <Button component={Link} to="/experts" variant="contained" size="small" sx={{ mt: 1 }}>
            Знайти експерта
          </Button>
        </Alert>

        <Paper sx={{ p: 3, my: 2 }}>
          <Typography variant="h5" gutterBottom component="div">Вода</Typography>
          <Grid container spacing={2} alignItems="flex-start">
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <OpacityIcon sx={{ color: '#1976d2', mr: 1, fontSize: '2.5rem' }} />
                <Typography variant="h4" sx={{ color: '#1976d2' }}>{data.waterMl ?? '—'} мл</Typography>
              </Box>
              <Typography variant="caption" sx={{ ml: '46px' }}>Розраховане значення</Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle1" gutterBottom>використані дані для розрахунків</Typography>
              {renderUsedData(calculationDetails?.water?.usedData)}
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle1" gutterBottom>використані калькулятори</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {calculationDetails?.water?.calculators?.map(calc => (
                  <Chip 
                    key={calc.name} 
                    label={calc.name} 
                    onDelete={calc.infoLink ? () => handleOpenInfoModal(calc.infoLink!) : undefined}
                    deleteIcon={calc.infoLink ? <InfoIcon /> : undefined}
                  />
                ))}
              </Box>
            </Grid>
          </Grid>
        </Paper>

        <Paper sx={{ p: 3, my: 2 }}>
          <Typography variant="h5" gutterBottom component="div">Калорії</Typography>
          <Grid container spacing={2} alignItems="flex-start">
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <BoltIcon sx={{ color: '#2e7d32', mr: 1, fontSize: '2.5rem' }} />
                <Typography variant="h4" sx={{ color: '#2e7d32' }}>{data.caloriesIntake ?? '—'} ккал</Typography>
              </Box>
              <Typography variant="caption" sx={{ ml: '46px' }}>Цільове споживання</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                <BoltIcon sx={{ color: '#d32f2f', mr: 1, fontSize: '1.5rem' }} />
                <Typography variant="h6" sx={{ color: '#d32f2f' }}>{data.caloriesBurned ?? '—'} ккал</Typography>
              </Box>
              <Typography variant="caption" sx={{ ml: '30px' }}>Витрата на активність</Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle1" gutterBottom>використані дані для розрахунків</Typography>
              {renderUsedData(calculationDetails?.calories?.usedData)}
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle1" gutterBottom>використані калькулятори</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {calculationDetails?.calories?.calculators?.map(calc => (
                  <Chip 
                    key={calc.name} 
                    label={calc.name} 
                    onDelete={calc.infoLink ? () => handleOpenInfoModal(calc.infoLink!) : undefined}
                    deleteIcon={calc.infoLink ? <InfoIcon /> : undefined}
                  />
                ))}
              </Box>
            </Grid>
          </Grid>
        </Paper>

        <Paper sx={{ p: 3, my: 2 }}>
          <Typography variant="h5" gutterBottom>Нутрієнти (БЖВ)</Typography>
          <Grid container spacing={2} alignItems="flex-start">
            <Grid item xs={12} md={4}>
              <Box sx={{ mb: 1 }}>
                <Typography variant="h5" component="span" sx={{ color: '#7CB342' }}>{data.proteinG == null ? '—' : `${data.proteinG} г (${proteinPercentage.toFixed(0)}%)`}</Typography>
                <Typography variant="caption" sx={{ ml: 1 }}>Білки</Typography>
              </Box>
              <Divider sx={{ my: 1 }} />
              <Box sx={{ mb: 1 }}>
                <Typography variant="h5" component="span" sx={{ color: '#FB8C00' }}>{data.fatG == null ? '—' : `${data.fatG} г (${fatPercentage.toFixed(0)}%)`}</Typography>
                <Typography variant="caption" sx={{ ml: 1 }}>Жири</Typography>
              </Box>
              <Divider sx={{ my: 1 }} />
              <Box>
                <Typography variant="h5" component="span" sx={{ color: '#D32F2F' }}>{data.carbsG == null ? '—' : `${data.carbsG} г (${carbsPercentage.toFixed(0)}%)`}</Typography>
                <Typography variant="caption" sx={{ ml: 1 }}>Вуглеводи</Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle1" gutterBottom>використані дані для розрахунків</Typography>
              {renderUsedData(calculationDetails?.nutrients?.usedData)}
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle1" gutterBottom>використані калькулятори</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {calculationDetails?.nutrients?.calculators?.map(calc => (
                  <Chip 
                    key={calc.name} 
                    label={calc.name} 
                    onDelete={calc.infoLink ? () => handleOpenInfoModal(calc.infoLink!) : undefined}
                    deleteIcon={calc.infoLink ? <InfoIcon /> : undefined}
                  />
                ))}
              </Box>
            </Grid>
          </Grid>
        </Paper>

        <Alert severity="warning" sx={{ mt: 3 }}>
          <AlertTitle>Автоматичний перерахунок</AlertTitle>
          Ці дані будуть автоматично перераховані у випадку зміни будь-яких вхідних параметрів у вашому профілі або програмі.
        </Alert>

        <MetricInfoModal
          open={isInfoModalOpen}
          onClose={handleCloseModal}
          metricName={selectedMetricForInfo || ''}
        />
      </Box>
    );
  };

  return (
    <DashboardLayout data-i-doc={PageIndices.SETTINGS}>
      <PageHint iDoc={PageIndices.SETTINGS} />
      <AdaptiveTabs tabs={tabs} searchParamKey="tab" defaultValue="profile" />

      <TabPanel value={currentTab} index="profile">
        <ProfileSettingsTab />
      </TabPanel>

      <TabPanel value={currentTab} index="units">
        {renderUnitsContent()}
      </TabPanel>

      <TabPanel value={currentTab} index="access">
        <AccessSettingsTab />
      </TabPanel>

      <TabPanel value={currentTab} index="interface">
        <InterfaceSettingsTab />
      </TabPanel>

      {isClient && (
        <TabPanel value={currentTab} index="goals">
          <Box data-i-doc={TabIndices.SETTINGS_GOALS}>
            <PageHint iDoc={TabIndices.SETTINGS_GOALS} />
            {renderGoalsContent()}
          </Box>
        </TabPanel>
      )}

      {isClient && (
        <TabPanel value={currentTab} index="goals-adv">
          <GoalsSettingsTab />
        </TabPanel>
      )}

      {isClient && (
        <TabPanel value={currentTab} index="subscription">
          <SubscriptionSettingsTab />
        </TabPanel>
      )}

      {isExpert && (
        <TabPanel value={currentTab} index="expert-subscription">
          <ExpertSubscriptionSettingsTab />
        </TabPanel>
      )}

      {isExpert && (
        <TabPanel value={currentTab} index="telegram">
          <TelegramSettingsTab />
        </TabPanel>
      )}

      {isExpert && (
        <TabPanel value={currentTab} index="mailings">
          <MailingsSettingsTab />
        </TabPanel>
      )}
    </DashboardLayout>
  );
};

export default SettingsPage;
